import { useState, useEffect } from "react";
import { Table, Button, Modal, message, Typography, Space, Divider, Form } from "antd";
import { DeleteOutlined, DownloadOutlined, CheckCircleTwoTone, SendOutlined } from "@ant-design/icons";
import { collection, getDocs, doc, getDoc, deleteDoc, updateDoc } from "firebase/firestore";
import { getStorage, ref, deleteObject, listAll } from "firebase/storage";
import { db } from "../firebase/firebase";
import TPCheckModal from "./medecins/TPCheckModal";

const { Title, Text } = Typography;

const Patients = ({ currentUserRole }) => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [correctionModalVisible, setCorrectionModalVisible] = useState(false);
  const [selectedCorrection, setSelectedCorrection] = useState(null);
  const [patientDetailsVisible, setPatientDetailsVisible] = useState(false);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [tpCheckModalVisible, setTpCheckModalVisible] = useState(false);
  const [tpCheckForm] = Form.useForm();
  const [alignersModalVisible, setAlignersModalVisible] = useState(false);
  const [tpCheckPatient, setTpCheckPatient] = useState(null);
  const [successAlignersModalVisible, setSuccessAlignersModalVisible] = useState(false);

  // Add these constants at the top, matching Medecins.jsx
  const PACK_OPTIONS = [
    'Lite arcade unique',
    'Lite deux arcades',
    'Essential arcade unique',
    'Essential deux arcades',
    'Smart arcade unique',
    'Smart deux arcades',
    'Pro',
    'Pro +'
  ];
  const PACK_PRICES = {
    "Lite arcade unique": 850,
    "Lite deux arcades": 1300,
    "Lite": 850,
    "Essential": 1150,
    "Essential deux arcades": 1950,
    "Essential arcade unique": 1150,
    "Smart": 2100,
    "Smart deux arcades": 3150,
    "Smart arcade unique": 2100,
    "Pro": 4500,
    "Pro +": 5000
  };
  const RHYTHM_OPTIONS = [7, 10, 14];

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        setLoading(true);
        // Get all patients
        const patientsSnapshot = await getDocs(collection(db, 'patients'));
        
        // Get all medecins for lookup
        const medecinsSnapshot = await getDocs(collection(db, 'medecins'));
        const medecinsMap = {};
        
        // Create a map of medecins with uid as key for quick lookup
        medecinsSnapshot.docs.forEach((doc) => {
          const medecinData = doc.data();
          if (medecinData.uid) {
            medecinsMap[medecinData.uid] = medecinData.name;
          }
        });
        
        // Map patient data with medecin name and decision status
        let patientsList = patientsSnapshot.docs.map((doc) => {
          const patientData = doc.data();
          let medecinName = "Not assigned";
          
          // Check if userId exists and find corresponding medecin
          if (patientData.userId && medecinsMap[patientData.userId]) {
            medecinName = medecinsMap[patientData.userId];
          }
          
          // Determine decision status
          let decisionStatus = "Pending";
          
          if (patientData.tpcheckValidated === true) {
            decisionStatus = "Validé";
          } else if (patientData.tpcheckValidated === false) {
            decisionStatus = "Non validé";
          } else if (patientData.correction) {
            decisionStatus = "Correction";
          }
          
          return {
            key: doc.id,
            ...patientData,
            medecinName: medecinName,
            decisionStatus: decisionStatus
          };
        });
        
        // Sort by addition date (prescriptionSubmitedAt or createdAt) descending
        patientsList = patientsList.sort((a, b) => {
          const dateA = a.prescriptionSubmitedAt || a.createdAt;
          const dateB = b.prescriptionSubmitedAt || b.createdAt;
          if (!dateA) return 1;
          if (!dateB) return -1;
          const timeA = dateA.toDate ? dateA.toDate().getTime() : new Date(dateA).getTime();
          const timeB = dateB.toDate ? dateB.toDate().getTime() : new Date(dateB).getTime();
          return timeB - timeA;
        });
        
        setPatients(patientsList);
      } catch (error) {
        /*console.error("Error fetching data:", error);*/
      } finally {
        setLoading(false);
      }
    };
    
    fetchPatients();
  }, []);

  const handleDelete = async (patient) => {
    setPatientToDelete(patient);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    if (!patientToDelete) return;

    setDeleting(true);
    try {
      // 1. Delete all files in the patient's storage folder
      const storage = getStorage();
      const patientFolderRef = ref(storage, `images/${patientToDelete.key}`);
      
      // List all files in the folder
      const filesList = await listAll(patientFolderRef);
      
      // Delete each file
      const deletePromises = filesList.items.map(fileRef => deleteObject(fileRef));
      await Promise.all(deletePromises);
      
      // 2. Delete the patient document from Firestore
      await deleteDoc(doc(db, "patients", patientToDelete.key));
      
      // 3. Update local state
      setPatients(prevPatients => prevPatients.filter(p => p.key !== patientToDelete.key));
      
      message.success('Patient deleted successfully');
    } catch (error) {
      /*console.error('Error deleting patient:', error);*/
      message.error('Failed to delete patient');
    } finally {
      setDeleting(false);
      setDeleteModalVisible(false);
      setPatientToDelete(null);
    }
  };

  const showCorrectionDetails = (patient) => {
    setSelectedCorrection(patient);
    setCorrectionModalVisible(true);
  };

  const showPatientDetails = (patient) => {
    setCurrentPatient(patient);
    setPatientDetailsVisible(true);
  };

  // Show TPCheck modal
  const showTPCheckModal = (patient) => {
    setPatientDetailsVisible(false); // Close the patient modal first
    setTimeout(() => {
      setTpCheckPatient(patient);
      // Set initial values as in Medecins.jsx
      const initialBasePrice = patient.price || (patient.pack ? PACK_PRICES[patient.pack] || 0 : 0);
      const initialDiscount = patient.discount || 0;
      const initialFinalPrice = (initialDiscount && Number(initialDiscount) > 0)
        ? initialBasePrice - (initialBasePrice * (initialDiscount / 100))
        : initialBasePrice;
      const initialValues = {
        TPCheck: patient.TPCheck || "",
        nbAligners: patient.nbAligners || 0, // <-- Add this line
        upperArcadeSteps: patient.upperArcadeSteps || 0,
        lowerArcadeSteps: patient.lowerArcadeSteps || 0,
        changeRhythm: patient.changeRhythm || RHYTHM_OPTIONS[0],
        pack: patient.pack || "",
        price: initialBasePrice,
        discount: initialDiscount,
        finalPrice: initialFinalPrice
      };
      tpCheckForm.setFieldsValue(initialValues);
      setTpCheckModalVisible(true);
    }, 300); // Wait for modal close animation
  };

  const handleTPCheckSubmit = async (values) => {
    try {
      const basePrice = values.price;
      const discount = values.discount;
      let discountedPrice = basePrice;
      if (discount && Number(discount) > 0) {
        discountedPrice = basePrice - (basePrice * (discount / 100));
      }
      let updateData = {
        TPCheck: values.TPCheck,
        nbAligners: values.nbAligners, // <-- Add this line
        upperArcadeSteps: values.upperArcadeSteps || 0,
        lowerArcadeSteps: values.lowerArcadeSteps || 0,
        changeRhythm: values.changeRhythm,
        pack: values.pack,
        price: basePrice,
        discount: discount,
        tpcheckDate: new Date().toISOString(),
        status: 2
      };
      const patientRef = doc(db, "patients", tpCheckPatient.key);
      await updateDoc(patientRef, updateData);
      setPatients(prevPatients =>
        prevPatients.map(patient =>
          patient.key === tpCheckPatient.key
            ? { ...patient, ...updateData }
            : patient
        )
      );
      Modal.success({
        title: 'Success',
        content: 'TPCheck information updated successfully',
      });
      tpCheckForm.resetFields();
      setTpCheckModalVisible(false);
      // Optionally refresh patient data from Firestore
      const patientDoc = await getDoc(patientRef);
      if (patientDoc.exists()) {
        const updatedPatient = { ...patientDoc.data(), key: tpCheckPatient.key };
        setCurrentPatient(updatedPatient);
      }
    } catch (error) {
      Modal.error({
        title: 'Error',
        content: 'Failed to update TPCheck information: ' + error.message,
      });
    }
  };

  const handlePackChange = (value) => {
    const price = PACK_PRICES[value] || 0;
    tpCheckForm.setFieldsValue({ price });
    // Calculate final price if discount exists
    const discount = tpCheckForm.getFieldValue('discount') || 0;
    const finalPrice = price - (price * (discount / 100));
    tpCheckForm.setFieldsValue({ finalPrice });
  };

  const handleDiscountChange = (value) => {
    const price = tpCheckForm.getFieldValue('price') || 0;
    const finalPrice = price - (price * (value / 100));
    tpCheckForm.setFieldsValue({ finalPrice });
  };

  // Handle sending aligners
  const handleSendAligners = async (patient) => {
    try {
      const patientRef = doc(db, "patients", patient.key);
      await updateDoc(patientRef, {
        alignersShippedDate: new Date().toISOString(),
      });
      setAlignersModalVisible(false);
      setPatientDetailsVisible(false);
      setTimeout(async () => {
        setSuccessAlignersModalVisible(true);
        // Refresh patients list and map required fields
        const patientsSnapshot = await getDocs(collection(db, 'patients'));
        // Get all medecins for lookup
        const medecinsSnapshot = await getDocs(collection(db, 'medecins'));
        const medecinsMap = {};
        medecinsSnapshot.docs.forEach((doc) => {
          const medecinData = doc.data();
          if (medecinData.uid) {
            medecinsMap[medecinData.uid] = medecinData.name;
          }
        });
        let patientsList = patientsSnapshot.docs.map((doc) => {
          const patientData = doc.data();
          let medecinName = "Not assigned";
          if (patientData.userId && medecinsMap[patientData.userId]) {
            medecinName = medecinsMap[patientData.userId];
          }
          let decisionStatus = "Pending";
          if (patientData.correction) {
            decisionStatus = "Correction";
          } else if (patientData.tpcheckValidated === false) {
            decisionStatus = "Non validé";
          } else if (patientData.tpcheckValidated === true) {
            decisionStatus = "Validé";
          }
          return {
            key: doc.id,
            ...patientData,
            medecinName: medecinName,
            decisionStatus: decisionStatus
          };
        });
        // Sort by addition date (prescriptionSubmitedAt or createdAt) descending
        patientsList = patientsList.sort((a, b) => {
          const dateA = a.prescriptionSubmitedAt || a.createdAt;
          const dateB = b.prescriptionSubmitedAt || b.createdAt;
          if (!dateA) return 1;
          if (!dateB) return -1;
          const timeA = dateA.toDate ? dateA.toDate().getTime() : new Date(dateA).getTime();
          const timeB = dateB.toDate ? dateB.toDate().getTime() : new Date(dateB).getTime();
          return timeB - timeA;
        });
        setPatients(patientsList);
      }, 300);
    } catch (error) {
      message.error("Erreur lors de la mise à jour");
    }
  };

  // Helper function to format teeth numbers
  const formatTeethNumbers = (teeth) => {
    if (!teeth || teeth.length === 0) return 'Aucune dent sélectionnée';
    return teeth.sort().join(', ');
  };

  // Helper function to format arcade text
  const formatArcadeText = (arcade, maxOption, mandOption) => {
    if (arcade === 'both') {
      return 'Les deux arcades';
    } else if (arcade === 'maxillaire') {
      return `Maxillaire${maxOption ? ` (Arcade opposée: ${
        maxOption === 'rien' ? 'Rien' : 
        maxOption === 'diagnostic' ? 'Diagnostic' : 
        maxOption === 'aligners' ? 'Aligners passifs' : ''
      })` : ''}`;
    } else if (arcade === 'mandibulaire') {
      return `Mandibulaire${mandOption ? ` (Arcade opposée: ${
        mandOption === 'rien' ? 'Rien' : 
        mandOption === 'diagnostic' ? 'Diagnostic' : 
        mandOption === 'aligners' ? 'Aligners passifs' : ''
      })` : ''}`;
    }
    return 'Non spécifié';
  };

  // Helper function to format rapport AP option
  const formatRapportAPOption = (option) => {
    switch (option) {
      case 'conserver': return 'Conserver la situation actuelle';
      case 'ameliorer_canine': return 'Améliorer la relation canine uniquement';
      case 'ameliorer_both': return 'Améliorer la relation canine & molaire';
      case 'correction': return 'Correction en Classe I';
      default: return option;
    }
  };

  // Helper function to format rapport AP
  const formatRapportAP = (rapportAP) => {
    if (!rapportAP) return 'Non spécifié';
    
    const text = [];

    if (rapportAP.D) {
      text.push(`Côté Droit: ${formatRapportAPOption(rapportAP.D)}`);
    }
    if (rapportAP.G) {
      text.push(`Côté Gauche: ${formatRapportAPOption(rapportAP.G)}`);
    }

    if (rapportAP.rip) {
      text.push(`RIP postérieure${rapportAP.ripPrecision ? ` (${rapportAP.ripPrecision})` : ''}`);
    }
    if (rapportAP.simulation) {
      text.push(`Simulation${rapportAP.simulationPrecision ? ` (${rapportAP.simulationPrecision})` : ''}`);
    }
    if (rapportAP.chirurgie) {
      text.push('Option chirurgicale');
    }

    return text.join('\n');
  };

  // Helper function to format espacement text
  const formatEspacementText = (prescriptionData) => {
    let text = [];

    // Add main espacement choice
    if (prescriptionData.espacement) {
      if (prescriptionData.espacement === 'fermer') {
        text.push("Fermer tous les espaces");
      } else if (prescriptionData.espacement === 'laisser') {
        text.push("Laisser des espaces spécifiques");
        if (prescriptionData.especesSpecifiques) {
          text.push(`Détails: ${prescriptionData.especesSpecifiques}`);
        }
      }
    }

    // Add maxillaire resolution details if any maxillaire data exists
    if (prescriptionData.expansion || prescriptionData.vestibuloversion || 
        prescriptionData.ripAnt || prescriptionData.ripPostDroite || prescriptionData.ripPostGauche) {
      text.push("\nRésolution au maxillaire:");
      if (prescriptionData.expansion) {
        text.push(`- Expansion: ${prescriptionData.expansion}`);
      }
      if (prescriptionData.vestibuloversion) {
        text.push(`- Vestibuloversion: ${prescriptionData.vestibuloversion}`);
      }
      if (prescriptionData.ripAnt) {
        text.push(`- RIP - Antérieure: ${prescriptionData.ripAnt}`);
      }
      if (prescriptionData.ripPostDroite) {
        text.push(`- RIP - Postérieure à droite: ${prescriptionData.ripPostDroite}`);
      }
      if (prescriptionData.ripPostGauche) {
        text.push(`- RIP - Postérieure à gauche: ${prescriptionData.ripPostGauche}`);
      }
    }

    // Add mandibulaire resolution details if any mandibulaire data exists
    if (prescriptionData.expansion2 || prescriptionData.vestibuloversion2 || 
        prescriptionData.ripAnt2 || prescriptionData.ripPostDroite2 || prescriptionData.ripPostGauche2) {
      text.push("\nRésolution à la mandibulaire:");
      if (prescriptionData.expansion2) {
        text.push(`- Expansion: ${prescriptionData.expansion2}`);
      }
      if (prescriptionData.vestibuloversion2) {
        text.push(`- Vestibuloversion: ${prescriptionData.vestibuloversion2}`);
      }
      if (prescriptionData.ripAnt2) {
        text.push(`- RIP - Antérieure: ${prescriptionData.ripAnt2}`);
      }
      if (prescriptionData.ripPostDroite2) {
        text.push(`- RIP - Postérieure à droite: ${prescriptionData.ripPostDroite2}`);
      }
      if (prescriptionData.ripPostGauche2) {
        text.push(`- RIP - Postérieure à gauche: ${prescriptionData.ripPostGauche2}`);
      }
    }

    // Add extractions information if present
    if (prescriptionData.extractions) {
      text.push("\nExtractions:");
      if (prescriptionData.extractions === 'none') {
        text.push("Aucune extraction");
      } else if (prescriptionData.extractions === 'some') {
        text.push("Extraire les dents suivantes:");
        if (prescriptionData.extractionsTeeth && prescriptionData.extractionsTeeth.length > 0) {
          text.push(`Dents sélectionnées: ${prescriptionData.extractionsTeeth.join(', ')}`);
        }
      }
    }

    return text.join('\n') || "Aucune information d'espacement spécifiée";
  };

  // Download function
  const downloadFile = (url, filename) => {
    if (!url) {
      Modal.error({ title: 'Error', content: 'No download URL provided' });
      return;
    }
    
    message.loading({ content: 'Preparing download...', key: 'download', duration: 0 });

    const fileExt = filename.split('.').pop().toLowerCase();
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt);
    const isStl = fileExt === 'stl';
    const isPly = fileExt === 'ply';
    const isPdf = fileExt === 'pdf';
    
    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        return response.blob();
      })
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        
        if (isStl || isPly || isPdf) {
          a.click();
          window.URL.revokeObjectURL(blobUrl);
          document.body.removeChild(a);
          message.success({ content: `${filename} download successful!`, key: 'download', duration: 2 });
        } else if (isImage) {
          window.open(blobUrl, '_blank');
          setTimeout(() => {
            a.click();
            window.URL.revokeObjectURL(blobUrl);
            document.body.removeChild(a);
            message.success({ content: `${filename} opened and downloading`, key: 'download', duration: 2 });
          }, 500);
        } else {
          a.click();
          window.URL.revokeObjectURL(blobUrl);
          document.body.removeChild(a);
          message.success({ content: `${filename} download successful!`, key: 'download', duration: 2 });
        }
      })
      .catch(error => {
        message.error({ content: 'Download failed', key: 'download', duration: 2 });
        try {
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          a.click();
          message.info({ content: 'Attempting alternate download method', key: 'download', duration: 2 });
        } catch (e) {
          window.open(url, '_blank');
          message.info({ content: 'Opening in new tab instead', key: 'download', duration: 2 });
        }
      });
  };

  const baseColumns = [
    {
      title: "Addition Date",
      dataIndex: "additionDate",
      key: "additionDate",
      render: (_, record) => {
        const date = record.prescriptionSubmitedAt || record.createdAt;
        if (!date) return 'N/A';
        const dateObj = date.toDate ? date.toDate() : new Date(date);
        return dateObj.toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
    },
    {
      title: "Patient ID",
      dataIndex: "key",
      key: "key",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Surname",
      dataIndex: "surname",
      key: "surname",
    },
    {
      title: "Assigned Medecin",
      dataIndex: "medecinName",
      key: "medecinName",
    },
    {
      title: "Decision Medecin",
      dataIndex: "decisionStatus",
      key: "decisionStatus",
      render: (text, record) => {
        const getStatusStyle = (status) => {
          switch (status) {
            case "Validé":
              return { 
                color: '#52c41a', 
                backgroundColor: '#f6ffed', 
                padding: '4px 8px', 
                borderRadius: '4px',
                border: '1px solid #b7eb8f',
                fontWeight: '500'
              };
            case "Non validé":
              return { 
                color: '#ff4d4f', 
                backgroundColor: '#fff2f0', 
                padding: '4px 8px', 
                borderRadius: '4px',
                border: '1px solid #ffccc7',
                fontWeight: '500'
              };
            case "Correction":
              return { 
                color: '#fa8c16', 
                backgroundColor: '#fff7e6', 
                padding: '4px 8px', 
                borderRadius: '4px',
                border: '1px solid #ffd591',
                fontWeight: '500',
                cursor: 'pointer',
                textDecoration: 'underline'
              };
            case "Pending":
            default:
              return { 
                color: '#8c8c8c', 
                backgroundColor: '#f5f5f5', 
                padding: '4px 8px', 
                borderRadius: '4px',
                border: '1px solid #d9d9d9',
                fontWeight: '500'
              };
          }
        };

        const style = getStatusStyle(text);
        
        if (text === "Correction") {
          return (
            <span 
              style={style}
              onClick={() => showCorrectionDetails(record)}
            >
              {text}
            </span>
          );
        }
        return <span style={style}>{text}</span>;
      }
    },
  ];

  const actionsColumn = {
    title: "Actions",
    key: "actions",
    render: (_, record) => (
      <Button
        type="text"
        danger
        icon={<DeleteOutlined />}
        onClick={() => handleDelete(record)}
      />
    ),
  };

  const columns = currentUserRole === 'superadmin' ? [...baseColumns, actionsColumn] : baseColumns;

  // Comprehensive patient details modal
  const PatientDetailsModal = () => {
    if (!currentPatient) return null;
    return (
      <Modal
        title={`Patient: ${currentPatient.name} ${currentPatient.surname || ''}`}
        open={patientDetailsVisible}
        onCancel={() => setPatientDetailsVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setPatientDetailsVisible(false)}>
            Close
          </Button>
        ]}
      >
        {/* Action buttons at top right */}
        <div style={{ position: 'absolute', top: 0, right: 56, zIndex: 1, marginTop: 12 }}>
          {currentPatient.alignersShippedDate ? (
            <Button
              key="aligners"
              type="primary"
              icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
              disabled
              style={{ marginRight: 8 }}
            >
              Aligners expédié
            </Button>
          ) : (
            <Button
              key="aligners"
              type="primary"
              icon={<SendOutlined />}
              onClick={() => setAlignersModalVisible(true)}
              style={{ marginRight: 8 }}
              disabled={!currentPatient || currentPatient.status < 3}
            >
              Envoyer Aligneurs
            </Button>
          )}
          <Button key="tpcheck" type="primary" onClick={() => showTPCheckModal(currentPatient)}>
            TPCheck
          </Button>
        </div>
        <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '8px' }}>
          {/* Photos Section */}
          <div style={{ marginBottom: '32px' }}>
            <Title level={4}>Photos</Title>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              {[1, 2, 3, 4, 6, 7, 8, 9, 10, 11].map(num => {
                const imgKey = `img${num}`;
                return currentPatient.images && currentPatient.images[imgKey] ? (
                  <div key={imgKey} style={{ marginBottom: '16px' }}>
                    <img 
                      src={currentPatient.images[imgKey]} 
                      alt={`Photo ${num}`} 
                      style={{ maxWidth: '200px', maxHeight: '200px' }} 
                    />
                    <div>
                      <Button 
                        icon={<DownloadOutlined />} 
                        onClick={() => downloadFile(currentPatient.images[imgKey], `photo_${num}.jpg`)}
                        size="small"
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                ) : null;
              })}
              {(!currentPatient.images || !Object.keys(currentPatient.images).some(key => {
                const num = parseInt(key.substring(3));
                return (num >= 1 && num <= 4) || (num >= 6 && num <= 11);
              })) && (
                <Text>No photos available</Text>
              )}
            </div>
          </div>
          
          {/* Scans Section */}
          <div style={{ marginBottom: '32px' }}>
            <Title level={4}>Scans</Title>
            <Space direction="vertical" style={{ width: '100%' }}>
              {currentPatient.images && currentPatient.images.img12 && (
                <div style={{ marginBottom: '16px' }}>
                  <Text strong>Scan 1</Text>
                  <div>
                    <Button 
                      icon={<DownloadOutlined />} 
                      onClick={() => downloadFile(
                        currentPatient.images.img12, 
                        `scan_1.${currentPatient.images.img12.toLowerCase().includes('.ply') ? 'ply' : 'stl'}`
                      )}
                    >
                      Download Scan 1
                    </Button>
                  </div>
                </div>
              )}
              
              {currentPatient.images && currentPatient.images.img13 && (
                <div style={{ marginBottom: '16px' }}>
                  <Text strong>Scan 2</Text>
                  <div>
                    <Button 
                      icon={<DownloadOutlined />} 
                      onClick={() => downloadFile(
                        currentPatient.images.img13, 
                        `scan_2.${currentPatient.images.img13.toLowerCase().includes('.ply') ? 'ply' : 'stl'}`
                      )}
                    >
                      Download Scan 2
                    </Button>
                  </div>
                </div>
              )}
              
              {(!currentPatient.images || (!currentPatient.images.img12 && !currentPatient.images.img13)) && (
                <Text>No scans available</Text>
              )}
            </Space>
          </div>
          
          {/* Prescription Section */}
          <div>
            <Title level={4}>Prescription Details</Title>
            <div style={{ padding: '16px', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {/* Basic Patient Info */}
                <div>
                  <Text strong>Patient Name:</Text> {currentPatient.name} {currentPatient.surname || ''}
                </div>
                <div>
                  <Text strong>Gender:</Text> {currentPatient.gender} 
                </div>
                <div>
                  <Text strong>Category:</Text> {currentPatient.category} 
                </div>
                <div>
                  <Text strong>Date of birth:</Text> {currentPatient.dateOfBirth} 
                </div>
                <div>
                  <Text strong>Assigned Medecin:</Text> {currentPatient.medecinName} 
                </div>
                <div>
                  <Text strong>Decision Status:</Text> {currentPatient.decisionStatus} 
                </div>
                
                {/* TPCheck Info */}
                {currentPatient.TPCheck && (
                  <div>
                    <Text strong>TPCheck Link:</Text> 
                    <a href={currentPatient.TPCheck} target="_blank" rel="noopener noreferrer">
                      {currentPatient.TPCheck}
                    </a>
                  </div>
                )}
                
                {currentPatient.nbAligners !== undefined && (
                  <div>
                    <Text strong>Number of Aligners:</Text> {currentPatient.nbAligners}
                  </div>
                )}
                
                {currentPatient.changeRhythm !== undefined && (
                  <div>
                    <Text strong>Change Rhythm (days):</Text> {currentPatient.changeRhythm}
                  </div>
                )}
                
                {currentPatient.pack !== undefined && (
                  <div>
                    <Text strong>Pack:</Text> {currentPatient.pack}
                  </div>
                )}
                
                {currentPatient.price !== undefined && (
                  <div>
                    <Text strong>Price:</Text> {currentPatient.price} €
                  </div>
                )}
                
                {/* Detailed Prescription Info */}
                {currentPatient.prescription && (
                  <>
                    <Divider orientation="left">Detailed Prescription</Divider>
                    
                    {/* 1. Arcade(s) à traiter */}
                    {currentPatient.prescription.arcade && (
                      <div>
                        <Text strong>1. Arcade(s) à traiter:</Text>
                        <div style={{ marginLeft: '20px' }}>
                          {formatArcadeText(
                            currentPatient.prescription.arcade,
                            currentPatient.prescription.maxillaireOption,
                            currentPatient.prescription.mandibulaireOption
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* 2. Restrictions des mouvements dentaires */}
                    {currentPatient.prescription.restrictions && (
                      <div>
                        <Text strong>2. Restrictions des mouvements dentaires:</Text>
                        <div style={{ marginLeft: '20px' }}>
                          {currentPatient.prescription.restrictions === 'none' 
                            ? 'Aucune restriction - Déplacer toutes les dents'
                            : currentPatient.prescription.restrictions === 'some' 
                              ? `Ne pas déplacer: ${formatTeethNumbers(currentPatient.prescription.restrictionsTeeth || currentPatient.prescription.selectedTeeth)}`
                              : 'Non spécifié'
                          }
                        </div>
                      </div>
                    )}
                    
                    {/* 3. Taquets */}
                    {currentPatient.prescription.taquets && (
                      <div>
                        <Text strong>3. Taquets:</Text>
                        <div style={{ marginLeft: '20px' }}>
                          {currentPatient.prescription.taquets === 'none'
                            ? 'Placer des taquets si nécessaire'
                            : currentPatient.prescription.taquets === 'some'
                              ? `Ne pas placer de taquets sur: ${formatTeethNumbers(currentPatient.prescription.taquetsTeeth)}`
                              : 'Non spécifié'
                          }
                        </div>
                      </div>
                    )}
                    
                    {/* Check if the assigned medecin is orthodontiste */}
                    {currentPatient.medecinName && currentPatient.medecinName.toLowerCase().includes('orthodontiste') ? (
                      <>
                        {/* 4. Rapport A-P */}
                        {currentPatient.prescription.rapportAP && (
                          <div>
                            <Text strong>4. Rapport antéro-postérieur:</Text>
                            <div style={{ marginLeft: '20px', whiteSpace: 'pre-line' }}>
                              {formatRapportAP(currentPatient.prescription.rapportAP)}
                            </div>
                          </div>
                        )}
                        
                        {/* 5. Overjet */}
                        {currentPatient.prescription.overjet && (
                          <div>
                            <Text strong>5. Overjet:</Text>
                            <div style={{ marginLeft: '20px' }}>
                              {currentPatient.prescription.overjet || 'Non spécifié'}
                            </div>
                          </div>
                        )}
                        
                        {/* 6. Overbite */}
                        {currentPatient.prescription.overbite && (
                          <div>
                            <Text strong>6. Overbite:</Text>
                            <div style={{ marginLeft: '20px' }}>
                              <div>{currentPatient.prescription.overbite || 'Non spécifié'}</div>
                              
                              {currentPatient.prescription.maxillaireOverbite?.selected && (
                                <div>
                                  Maxillaire:
                                  {currentPatient.prescription.maxillaireOverbite.egressionAnterieure && 
                                    <div style={{ marginLeft: '10px' }}>- Égression des dents antérieures</div>}
                                  {currentPatient.prescription.maxillaireOverbite.ingressionPosterieure && 
                                    <div style={{ marginLeft: '10px' }}>- Ingression des dents postérieures</div>}
                                </div>
                              )}
                              
                              {currentPatient.prescription.mandibulaireOverbite?.selected && (
                                <div>
                                  Mandibulaire:
                                  {currentPatient.prescription.mandibulaireOverbite.egressionAnterieure && 
                                    <div style={{ marginLeft: '10px' }}>- Égression des dents antérieures</div>}
                                  {currentPatient.prescription.mandibulaireOverbite.ingressionPosterieure && 
                                    <div style={{ marginLeft: '10px' }}>- Ingression des dents postérieures</div>}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* 7. Bite Ramps */}
                        {currentPatient.prescription.biteRamps && (
                          <div>
                            <Text strong>7. Bite Ramps:</Text>
                            <div style={{ marginLeft: '20px' }}>
                              <div>{currentPatient.prescription.biteRamps}</div>
                              {Array.isArray(currentPatient.prescription.biteRampsOptions) && 
                               currentPatient.prescription.biteRampsOptions.length > 0 && (
                                <div>Options: {currentPatient.prescription.biteRampsOptions.join(', ')}</div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* 8. Milieux */}
                        {currentPatient.prescription.milieux && (
                          <div>
                            <Text strong>8. Milieux inter-incisifs:</Text>
                            <div style={{ marginLeft: '20px' }}>
                              <div>
                                {currentPatient.prescription.milieux === 'realiser' 
                                  ? 'Réaliser les autres objectifs et j\'évaluerai les milieux inter-incisifs'
                                  : currentPatient.prescription.milieux === 'maintenir'
                                    ? 'Maintenir les milieux inter-incisifs d\'origine'
                                    : currentPatient.prescription.milieux === 'ameliorer'
                                      ? 'Améliorer les milieux inter-incisifs par IPR'
                                      : 'Non spécifié'
                                }
                              </div>
                              {currentPatient.prescription.milieuxOptions && currentPatient.prescription.milieuxOptions.length > 0 && (
                                <div>
                                  {currentPatient.prescription.milieuxOptions.includes('maxillaire') && (
                                    <div>
                                      Maxillaire:
                                      {currentPatient.prescription.milieuxOptions.includes('maxillaireDirection-droite') && ' vers la droite'}
                                      {currentPatient.prescription.milieuxOptions.includes('maxillaireDirection-gauche') && ' vers la gauche'}
                                    </div>
                                  )}
                                  {currentPatient.prescription.milieuxOptions.includes('mandibulaire') && (
                                    <div>
                                      Mandibulaire:
                                      {currentPatient.prescription.milieuxOptions.includes('mandibulaireDirection-droite') && ' vers la droite'}
                                      {currentPatient.prescription.milieuxOptions.includes('mandibulaireDirection-gauche') && ' vers la gauche'}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* 9. Articulé croisé postérieur */}
                        {currentPatient.prescription.articule && (
                          <div>
                            <Text strong>9. Articulé croisé postérieur:</Text>
                            <div style={{ marginLeft: '20px' }}>
                              {currentPatient.prescription.articule === 'none' ? 'Ne pas corriger' :
                               currentPatient.prescription.articule === 'some' ? 'Corriger' :
                               'Non spécifié'}
                            </div>
                          </div>
                        )}
                        
                        {/* 10. Espacement et Encombrement */}
                        {(currentPatient.prescription.espacement || 
                          currentPatient.prescription.expansion || 
                          currentPatient.prescription.expansion2 || 
                          currentPatient.prescription.vestibuloversion ||
                          currentPatient.prescription.vestibuloversion2 ||
                          currentPatient.prescription.ripAnt ||
                          currentPatient.prescription.ripAnt2 ||
                          currentPatient.prescription.ripPostDroite ||
                          currentPatient.prescription.ripPostDroite2 ||
                          currentPatient.prescription.ripPostGauche ||
                          currentPatient.prescription.ripPostGauche2 ||
                          currentPatient.prescription.extractions) && (
                          <div>
                            <Text strong>10. Espacement et Encombrement (DDM):</Text>
                            <div style={{ marginLeft: '20px', whiteSpace: 'pre-line' }}>
                              {formatEspacementText(currentPatient.prescription)}
                            </div>
                          </div>
                        )}
                        
                        {/* 11. Instructions Spéciales */}
                        {currentPatient.prescription.specialInstructions && (
                          <div>
                            <Text strong>11. Instructions Spéciales:</Text>
                            <div style={{ marginLeft: '20px', whiteSpace: 'pre-wrap' }}>
                              {typeof currentPatient.prescription.specialInstructions === 'string' 
                                ? currentPatient.prescription.specialInstructions 
                                : 'Aucune instruction spéciale'}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {/* 4. Milieux */}
                        {currentPatient.prescription.milieux && (
                          <div>
                            <Text strong>4. Milieux inter-incisifs:</Text>
                            <div style={{ marginLeft: '20px' }}>
                              <div>
                                {currentPatient.prescription.milieux === 'realiser' 
                                  ? 'Réaliser les autres objectifs et j\'évaluerai les milieux inter-incisifs'
                                  : currentPatient.prescription.milieux === 'maintenir'
                                    ? 'Maintenir les milieux inter-incisifs d\'origine'
                                    : currentPatient.prescription.milieux === 'ameliorer'
                                      ? 'Améliorer les milieux inter-incisifs par IPR'
                                      : 'Non spécifié'
                                }
                              </div>
                              {currentPatient.prescription.milieuxOptions && currentPatient.prescription.milieuxOptions.length > 0 && (
                                <div>
                                  {currentPatient.prescription.milieuxOptions.includes('maxillaire') && (
                                    <div>
                                      Maxillaire:
                                      {currentPatient.prescription.milieuxOptions.includes('maxillaireDirection-droite') && ' vers la droite'}
                                      {currentPatient.prescription.milieuxOptions.includes('maxillaireDirection-gauche') && ' vers la gauche'}
                                    </div>
                                  )}
                                  {currentPatient.prescription.milieuxOptions.includes('mandibulaire') && (
                                    <div>
                                      Mandibulaire:
                                      {currentPatient.prescription.milieuxOptions.includes('mandibulaireDirection-droite') && ' vers la droite'}
                                      {currentPatient.prescription.milieuxOptions.includes('mandibulaireDirection-gauche') && ' vers la gauche'}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* 5. Espacement et Encombrement */}
                        {(currentPatient.prescription.espacement || 
                          currentPatient.prescription.expansion || 
                          currentPatient.prescription.expansion2 || 
                          currentPatient.prescription.vestibuloversion ||
                          currentPatient.prescription.vestibuloversion2 ||
                          currentPatient.prescription.ripAnt ||
                          currentPatient.prescription.ripAnt2 ||
                          currentPatient.prescription.ripPostDroite ||
                          currentPatient.prescription.ripPostDroite2 ||
                          currentPatient.prescription.ripPostGauche ||
                          currentPatient.prescription.ripPostGauche2 ||
                          currentPatient.prescription.extractions) && (
                          <div>
                            <Text strong>5. Espacement et Encombrement (DDM):</Text>
                            <div style={{ marginLeft: '20px', whiteSpace: 'pre-line' }}>
                              {formatEspacementText(currentPatient.prescription)}
                            </div>
                          </div>
                        )}
                        
                        {/* 6. Instructions Spéciales */}
                        {currentPatient.prescription.specialInstructions && (
                          <div>
                            <Text strong>6. Instructions Spéciales:</Text>
                            <div style={{ marginLeft: '20px', whiteSpace: 'pre-wrap' }}>
                              {typeof currentPatient.prescription.specialInstructions === 'string' 
                                ? currentPatient.prescription.specialInstructions 
                                : 'Aucune instruction spéciale'}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
                
                {/* Notes */}
                {currentPatient.notes && (
                  <div>
                    <Text strong>Notes:</Text>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{currentPatient.notes}</div>
                  </div>
                )}
                
                {/* Prescription Document */}
                {currentPatient.prescription && currentPatient.prescription.url && (
                  <div style={{ marginTop: '16px' }}>
                    <Button 
                      icon={<DownloadOutlined />} 
                      onClick={() => downloadFile(currentPatient.prescription.url, "prescription.pdf")}
                    >
                      Download Prescription Document
                    </Button>
                  </div>
                )}

                {currentPatient.tpcheckDate && (
                  <div>
                    <Text strong>TPCheck Date:</Text> {new Date(currentPatient.tpcheckDate).toLocaleDateString('fr-FR')}
                  </div>
                )}
                
                {currentPatient.alignersShippedDate && (
                  <div>
                    <Text strong>Aligners Shipped Date:</Text> {new Date(currentPatient.alignersShippedDate).toLocaleDateString('fr-FR')}
                  </div>
                )}
              </Space>
            </div>
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <>
      <Table
        dataSource={patients}
        columns={columns}
        rowKey="key"
        loading={loading}
        title={() => "Patients List"}
        onRow={(record) => ({
          onClick: () => showPatientDetails(record),
          style: { cursor: 'pointer' }
        })}
      />

      <PatientDetailsModal />

      {/* TPCheck Modal rendered at root level for stacking */}
      <TPCheckModal
        open={tpCheckModalVisible}
        onCancel={() => setTpCheckModalVisible(false)}
        onFinish={handleTPCheckSubmit}
        form={tpCheckForm}
        patient={tpCheckPatient}
        PACK_OPTIONS={PACK_OPTIONS}
        RHYTHM_OPTIONS={RHYTHM_OPTIONS}
        handlePackChange={handlePackChange}
        handleDiscountChange={handleDiscountChange}
        zIndex={2000}
        useNbAligners // <-- Custom prop to indicate Patients.jsx context
      />

      {/* Envoyer Aligneurs Modal */}
      <Modal
        title="Confirmer l'envoi des aligners"
        open={alignersModalVisible}
        onOk={async () => {
          await handleSendAligners(currentPatient);
        }}
        onCancel={() => setAlignersModalVisible(false)}
        okText="Confirmer"
        cancelText="Annuler"
      >
        <p>Êtes-vous sûr de vouloir marquer les aligners comme envoyés pour ce patient ?</p>
      </Modal>

      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setPatientToDelete(null);
        }}
        confirmLoading={deleting}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete this patient? This action cannot be undone.</p>
        {patientToDelete && (
          <p>
            Patient: {patientToDelete.name} {patientToDelete.surname}
          </p>
        )}
      </Modal>

      <Modal
        title="Correction Details"
        open={correctionModalVisible}
        onCancel={() => {
          setCorrectionModalVisible(false);
          setSelectedCorrection(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setCorrectionModalVisible(false);
            setSelectedCorrection(null);
          }}>
            Close
          </Button>
        ]}
      >
        {selectedCorrection && (
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#f5f5f5', 
            borderRadius: '4px',
            whiteSpace: 'pre-wrap'
          }}>
            {selectedCorrection.correction}
          </div>
        )}
      </Modal>
      {/* Custom Success Modal for Aligners */}
      <Modal
        title="Succès"
        open={successAlignersModalVisible}
        onOk={() => setSuccessAlignersModalVisible(false)}
        onCancel={() => setSuccessAlignersModalVisible(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setSuccessAlignersModalVisible(false)}>
            OK
          </Button>
        ]}
        centered
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }}>✓</div>
          <p style={{ fontSize: '16px', margin: 0 }}>
            Aligners expédié successfully
          </p>
        </div>
      </Modal>
    </>
  );
};

export default Patients;